import { AnimatePresence, motion } from 'framer-motion';
import { Typography } from '@tempmee/react';
import { CenteredSpinner } from '~/commonComponents';
import { colors } from '@tempmee/tailwind';
import { ProfessionalHubRedesign } from '../../../../constants/ff.posthog';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';
import { buildCardRating } from '../../buildCardRating';
import { CardActionsHeader } from '../CardActionsHeader';
import { EmptyFilteredList } from '../EmptyFilteredList';
import { HubCardList } from '../shared/HubCardList';
import { ProfessionalCard } from '../ProfessionalCard';
import { EmptyFavorites } from './EmptyFavorites';
// New design
import { ProfessionalCardDetailsNew } from './ProfessionalCardDetailsNew';
import { ProfessionalCardActionsNew } from './ProfessionalCardActionsNew';
// Legacy design
import { ProfessionalCardDetails as ProfessionalCardDetailsLegacy } from './ProfessionalCardDetails';
import { ProfessionalCardActions as ProfessionalCardActionsLegacy } from './ProfessionalCardActions';
import { useFavoritesList } from './hooks/useFavoritesList';

export const FavoritesList = () => {
  const isNewDesign = useFeatureFlag(ProfessionalHubRedesign);
  const { isLoading, favoriteProfessionals, isEmpty, isFiltered } = useFavoritesList();

  if (isLoading) {
    return <CenteredSpinner />;
  }

  if (isEmpty) {
    if (isFiltered) {
      return <EmptyFilteredList />;
    }
    return <EmptyFavorites />;
  }

  // Legacy design (feature flag OFF)
  if (!isNewDesign) {
    return (
      <div
        style={{
          width: '100%',
        }}
      >
        <Typography
          italic
          color={colors.neutral[500]}
          style={{ display: 'flex', textAlign: 'center', justifyContent: 'center', width: '100%' }}
        >
          You can manage your list of favorite professionals on this section of the Professionals
          Hub,
          <br />
          and directly invite them to your upcoming shifts!
        </Typography>

        <div
          style={{
            maxWidth: '827px',
            marginTop: 32,
            display: 'flex',
            justifyContent: 'center',
            marginLeft: 'auto',
            marginRight: 'auto',
          }}
        >
          <motion.div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
            }}
            layout
          >
            <CardActionsHeader actions={['Invite']} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
              <AnimatePresence>
                {favoriteProfessionals?.map((professional) => (
                  <ProfessionalCard
                    key={professional.id}
                    user={{
                      ...professional,
                      isFavorite: true,
                      rating: buildCardRating(professional?.averageProfessionalRating),
                    }}
                    Details={ProfessionalCardDetailsLegacy}
                    Actions={ProfessionalCardActionsLegacy}
                    showRemoveIcon
                  />
                ))}
              </AnimatePresence>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  // New design (feature flag ON)
  return (
    <HubCardList
      headerActions={['Invite']}
      headerRightOffset={52}
      intro="You can manage your list of favorite professionals on this section of the Professionals Hub, and directly invite them to your upcoming shifts!"
    >
      {favoriteProfessionals?.map((professional) => (
        <ProfessionalCard
          key={professional.id}
          user={{
            id: professional.id,
            fullname: professional.fullname,
            professions: professional.professions,
            profession: professional.professions?.[0],
            profileImage: professional.profileImage,
            isAvailable: professional.isAvailable ?? true,
            isFavorite: true,
            yearsOfExperience: professional.yearsOfExperience,
            badges: professional.badges ?? [],
            activityLevel: professional.activityLevel,
            rating: buildCardRating(professional?.averageProfessionalRating),
          }}
          Details={ProfessionalCardDetailsNew}
          Actions={ProfessionalCardActionsNew}
          isNewDesign
          isFavoriteCard
        />
      ))}
    </HubCardList>
  );
};
